import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Filter,
  Download,
  ArrowUpDown,
  Building
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import { useMyEcheanciers, useMySouscriptions } from '../../hooks/useClientApi';
import { Echeancier } from '../../types/api';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useTexts } from '../../hooks/useTexts';

const EcheanciersPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const subscriptionFromUrl = searchParams.get('subscription');
  const { getText } = useTexts();
  
  const [filters, setFilters] = useState({
    statut_echeance: '',
    date_echeance__gte: '',
    date_echeance__lte: '',
    souscription: subscriptionFromUrl || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    statut_echeance: '',
    date_echeance__gte: '',
    date_echeance__lte: '',
    souscription: subscriptionFromUrl || '',
  });
  const [sortBy, setSortBy] = useState('date_echeance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const { data: echeanciersData, loading, error, refetch } = useMyEcheanciers(appliedFilters, { immediate: true });
  const { data: souscriptionsData } = useMySouscriptions({}, { immediate: true });

  // Fonction pour appliquer les filtres manuellement
  const applyFilters = () => {
    // Valider les dates avant d'appliquer les filtres
    const hasInvalidDate = Object.entries(filters).some(([key, value]) => {
      if (key.includes('date_echeance') && value) {
        return !isCompleteDate(value);
      }
      return false;
    });

    if (hasInvalidDate) {
      alert('Veuillez saisir des dates valides (année entre 1900 et 2099)');
      return;
    }

    // Supprimer le paramètre subscription de l'URL quand l'utilisateur applique de nouveaux filtres
    if (subscriptionFromUrl) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    }

    setAppliedFilters(filters);
  };

  // Appliquer automatiquement le filtre de souscription depuis l'URL
  useEffect(() => {
    if (subscriptionFromUrl && subscriptionFromUrl !== appliedFilters.souscription) {
      setAppliedFilters(prev => ({
        ...prev,
        souscription: subscriptionFromUrl
      }));
    }
  }, [subscriptionFromUrl, appliedFilters.souscription]);

  // Déclencher le refetch quand les filtres appliqués changent
  useEffect(() => {
    refetch();
  }, [appliedFilters, refetch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Fonction de formatage spécifique pour le PDF
  const formatCurrencyForPDF = (amount: number) => {
    // Formatage avec point comme séparateur de milliers
    return amount.toLocaleString('fr-FR').replace(/\s/g, '.');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'PAYE':
        return 'bg-green-100 text-green-800';
      case 'EN_ATTENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'EN_RETARD':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (statut: string) => {
    switch (statut) {
      case 'PAYE':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'EN_ATTENTE':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'EN_RETARD':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  // Fonction pour charger une image et la convertir en base64
  const loadImageAsBase64 = (url: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  // Fonction pour exporter les échéanciers en PDF
  const exportToPDF = async () => {
    // Charger le logo
    const logoBase64 = await loadImageAsBase64('/media/company/logo.png');

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Informations de l'entreprise
    const companyInfo = {
      name: 'SOGEIMCO',
      fullName: 'Société de Gestion Immobilière et de Construction',
      address: 'Abidjan, Côte d\'Ivoire',
      phone: '+225 27 24 31 45 27',
      email: 'info@sogeimco.com',
      website: 'www.sogeimco.com'
    };

    // Fonction pour dessiner l'en-tête
    const drawHeader = () => {
      // Fond de l'en-tête
      doc.setFillColor(44, 82, 130);
      doc.rect(0, 0, pageWidth, 25, 'F');

      // Logo
      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', 10, 3, 19, 19);
        } catch (e) {
          console.error('Erreur lors de l\'ajout du logo:', e);
        }
      }

      // Nom de l'entreprise (décalé si logo présent)
      const textX = logoBase64 ? 32 : 14;
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(companyInfo.name, textX, 12);

      // Sous-titre
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(companyInfo.fullName, textX, 18);

      // Réinitialiser les couleurs
      doc.setTextColor(0, 0, 0);
    };

    // Fonction pour dessiner le pied de page
    const drawFooter = (pageNumber: number, totalPages: number) => {
      const footerY = pageHeight - 15;

      // Ligne de séparation
      doc.setDrawColor(44, 82, 130);
      doc.setLineWidth(0.5);
      doc.line(14, footerY - 5, pageWidth - 14, footerY - 5);

      // Informations de contact
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`${companyInfo.address} | Tél: ${companyInfo.phone} | Email: ${companyInfo.email}`, 14, footerY);

      // Numéro de page
      doc.text(`Page ${pageNumber} / ${totalPages}`, pageWidth - 30, footerY);

      doc.setTextColor(0, 0, 0);
    };

    // Dessiner l'en-tête de la première page
    drawHeader();

    // Titre du document (après l'en-tête)
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(44, 82, 130);
    doc.text('Échéancier de Paiement', 14, 38);

    // Date d'export
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, 14, 45);
    doc.setTextColor(0, 0, 0);

    let yPosition = 55;

    // Informations de filtrage
    doc.setFontSize(11);
    if (appliedFilters.souscription) {
      const souscription = souscriptionsData?.results?.find(s => s.id.toString() === appliedFilters.souscription);
      if (souscription) {
        doc.text(`Souscription: ${souscription.type_maison_designation} - ${souscription.projet_nom}`, 14, yPosition);
        yPosition += 7;
      }
    }

    if (appliedFilters.date_echeance__gte || appliedFilters.date_echeance__lte) {
      let dateText = 'Période: ';
      if (appliedFilters.date_echeance__gte) dateText += `du ${formatDate(appliedFilters.date_echeance__gte)}`;
      if (appliedFilters.date_echeance__lte) dateText += ` au ${formatDate(appliedFilters.date_echeance__lte)}`;
      doc.text(dateText, 14, yPosition);
      yPosition += 7;
    }

    // Ligne de séparation
    yPosition += 3;
    doc.line(14, yPosition, 196, yPosition);
    yPosition += 10;

    // === BILAN DES ÉCHÉANCES ===
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bilan des échéances', 14, yPosition);
    yPosition += 10;

    // Calculer les statistiques sur les échéances filtrées
    const paidCount = filteredEcheanciers.filter((e: Echeancier) => e.statut_echeance === 'PAYE').length;
    const pendingCount = filteredEcheanciers.filter((e: Echeancier) => e.statut_echeance === 'EN_ATTENTE').length;
    const overdueCount = filteredEcheanciers.filter((e: Echeancier) => e.statut_echeance === 'EN_RETARD').length;

    const paidAmount = filteredEcheanciers
      .filter((e: Echeancier) => e.statut_echeance === 'PAYE')
      .reduce((sum: number, e: Echeancier) => sum + Number(e.montant_prevu), 0);
    const pendingAmount = filteredEcheanciers
      .filter((e: Echeancier) => e.statut_echeance === 'EN_ATTENTE')
      .reduce((sum: number, e: Echeancier) => sum + Number(e.montant_prevu), 0);
    const overdueAmount = filteredEcheanciers
      .filter((e: Echeancier) => e.statut_echeance === 'EN_RETARD')
      .reduce((sum: number, e: Echeancier) => sum + Number(e.montant_prevu), 0);
    const totalFilteredAmount = filteredEcheanciers
      .reduce((sum: number, e: Echeancier) => sum + Number(e.montant_prevu), 0);

    // Tableau du bilan
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // En-têtes du bilan
    doc.setFillColor(59, 130, 246);
    doc.setTextColor(255, 255, 255);
    doc.rect(14, yPosition, 182, 8, 'F');
    doc.text('Statut', 20, yPosition + 5.5);
    doc.text('Nombre', 80, yPosition + 5.5);
    doc.text('Montant (FCFA)', 130, yPosition + 5.5);
    yPosition += 8;

    doc.setTextColor(0, 0, 0);

    // Ligne Payé
    doc.setFillColor(220, 252, 231);
    doc.rect(14, yPosition, 182, 7, 'F');
    doc.setTextColor(22, 101, 52);
    doc.text('Payé', 20, yPosition + 5);
    doc.text(paidCount.toString(), 80, yPosition + 5);
    doc.text(formatCurrencyForPDF(paidAmount), 130, yPosition + 5);
    yPosition += 7;

    // Ligne En attente
    doc.setFillColor(254, 249, 195);
    doc.rect(14, yPosition, 182, 7, 'F');
    doc.setTextColor(133, 100, 4);
    doc.text('En attente', 20, yPosition + 5);
    doc.text(pendingCount.toString(), 80, yPosition + 5);
    doc.text(formatCurrencyForPDF(pendingAmount), 130, yPosition + 5);
    yPosition += 7;

    // Ligne En retard
    doc.setFillColor(254, 226, 226);
    doc.rect(14, yPosition, 182, 7, 'F');
    doc.setTextColor(153, 27, 27);
    doc.text('En retard', 20, yPosition + 5);
    doc.text(overdueCount.toString(), 80, yPosition + 5);
    doc.text(formatCurrencyForPDF(overdueAmount), 130, yPosition + 5);
    yPosition += 7;

    // Ligne Total
    doc.setFillColor(229, 231, 235);
    doc.rect(14, yPosition, 182, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', 20, yPosition + 5.5);
    doc.text(filteredEcheanciers.length.toString(), 80, yPosition + 5.5);
    doc.text(formatCurrencyForPDF(totalFilteredAmount), 130, yPosition + 5.5);
    yPosition += 12;

    doc.setFont('helvetica', 'normal');

    // Ligne de séparation avant le détail
    yPosition += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Détail des échéances', 14, yPosition);
    doc.setFont('helvetica', 'normal');
    yPosition += 8;
    
    // Préparer les données du tableau
    const tableData = filteredEcheanciers.map((echeance: Echeancier) => {
      // Trouver la souscription correspondante
      const souscription = souscriptionsData?.results?.find(s => s.id === echeance.souscription);
      
      return [
        echeance.numero_echeance.toString(),
        formatDate(echeance.date_echeance),
        formatCurrencyForPDF(Number(echeance.montant_prevu)),
        {
          'EN_ATTENTE': 'En attente',
          'PAYE': 'Payé',
          'EN_RETARD': 'En retard'
        }[echeance.statut_echeance] || echeance.statut_echeance,
        souscription?.type_maison_designation || 'N/A',
        souscription?.projet_nom || 'N/A'
      ];
    });
    
    // Créer le tableau
    autoTable(doc, {
      startY: yPosition,
      head: [['N°', 'Date d\'échéance', 'Montant(Fcfa)', 'Statut', 'Type de maison', 'Projet']],
      body: tableData,
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246], // Bleu
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252], // Gris très clair
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20 }, // N°
        1: { halign: 'center', cellWidth: 30 }, // Date
        2: { halign: 'right', cellWidth: 35 },  // Montant
        3: { halign: 'center', cellWidth: 25 }, // Statut
        4: { cellWidth: 40 }, // Type de maison
        5: { cellWidth: 40 }, // Projet
      },
      margin: { top: 30, left: 14, right: 14, bottom: 25 },
      didDrawPage: (data: any) => {
        // Dessiner l'en-tête sur chaque page (sauf la première qui l'a déjà)
        if (data.pageNumber > 1) {
          drawHeader();
        }
      },
    });

    // Ajouter le pied de page sur toutes les pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawFooter(i, totalPages);
    }

    // Sauvegarder le PDF
    const fileName = `echeancier_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  // Fonction pour valider si une date est complète et valide
  const isCompleteDate = (dateString: string): boolean => {
    if (!dateString) return true; // Permet de vider le champ
    
    // Vérifier d'abord si c'est une date complète (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      // Si ce n'est pas une date complète, vérifier si c'est une date partielle avec une année valide
      const partialDateRegex = /^\d{4}(-\d{2})?(-\d{2})?$/;
      if (partialDateRegex.test(dateString)) {
        const year = parseInt(dateString.split('-')[0]);
        // Si l'année est invalide, retourner false même pour les dates partielles
        if (year < 1900 || year > 2099) return false;
      }
      return false; // Date incomplète
    }
    
    // Vérifier que l'année est entre 1900 et 2099
    const year = parseInt(dateString.split('-')[0]);
    if (year < 1900 || year > 2099) return false;
    
    // Vérifier que la date est valide (pas le 32 janvier par exemple)
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && date.toISOString().split('T')[0] === dateString;
  };

  const handleFilterChange = (key: string, value: string) => {
    // Si l'utilisateur change le filtre de souscription manuellement, supprimer le paramètre URL
    if (key === 'souscription' && subscriptionFromUrl) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    }
    
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    const emptyFilters = {
      statut_echeance: '',
      date_echeance__gte: '',
      date_echeance__lte: '',
      souscription: '',
    };
    
    // Supprimer le paramètre subscription de l'URL lors de la réinitialisation
    if (subscriptionFromUrl) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    }
    
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Trier les échéances
  const filteredEcheanciers = (echeanciersData?.results || [])
    .sort((a: Echeancier, b: Echeancier) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'numero_echeance':
          aValue = a.numero_echeance;
          bValue = b.numero_echeance;
          break;
        case 'date_echeance':
          aValue = new Date(a.date_echeance);
          bValue = new Date(b.date_echeance);
          break;
        case 'montant_prevu':
          aValue = Number(a.montant_prevu);
          bValue = Number(b.montant_prevu);
          break;
        case 'statut_echeance':
          aValue = a.statut_echeance;
          bValue = b.statut_echeance;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage error={error} onDismiss={() => refetch()} />
      </div>
    );
  }

  const echeanciers = echeanciersData?.results || [];

  // Calculer les statistiques
  const totalEcheances = echeanciers.length;
  const paidEcheances = echeanciers.filter(e => e.statut_echeance === 'PAYE').length;
  const pendingEcheances = echeanciers.filter(e => e.statut_echeance === 'EN_ATTENTE').length;
  const overdueEcheances = echeanciers.filter(e => e.statut_echeance === 'EN_RETARD').length;
  const totalAmount = echeanciers.reduce((sum, e) => sum + Number(e.montant_prevu), 0);
  const paidAmount = echeanciers.filter(e => e.statut_echeance === 'PAYE').reduce((sum, e) => sum + Number(e.montant_prevu), 0);
  const pendingAmount = echeanciers.filter(e => e.statut_echeance === 'EN_ATTENTE').reduce((sum, e) => sum + Number(e.montant_prevu), 0);
  const overdueAmount = echeanciers.filter(e => e.statut_echeance === 'EN_RETARD').reduce((sum, e) => sum + Number(e.montant_prevu), 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('echeanciers.title'), current: true }
            ]}
          />
        </div>

        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {getText('echeanciers.title')}
          </h1>
          <p className="text-gray-600">
            {getText('echeanciers.description')}
          </p>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                {getText('echeanciers.filters')}
              </h3>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {getText('echeanciers.search')}
              </button>
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {getText('echeanciers.reset')}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.statut_echeance}
                onChange={(e) => handleFilterChange('statut_echeance', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous les statuts</option>
                <option value="EN_ATTENTE">En attente</option>
                <option value="PAYE">Payé</option>
                <option value="EN_RETARD">En retard</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Souscription
              </label>
              <select
                value={filters.souscription}
                onChange={(e) => handleFilterChange('souscription', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes les souscriptions</option>
                {souscriptionsData?.results?.map((souscription: any) => (
                  <option key={souscription.id} value={souscription.id}>
                    {souscription.type_maison_designation} - {souscription.projet_nom} (Niveau {souscription.niveau})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                type="date"
                value={filters.date_echeance__gte}
                onChange={(e) => handleFilterChange('date_echeance__gte', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                type="date"
                value={filters.date_echeance__lte}
                onChange={(e) => handleFilterChange('date_echeance__lte', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
      </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Échéances</p>
                <p className="text-2xl font-bold text-gray-900">{totalEcheances}</p>
                <p className="text-sm text-blue-600 font-medium">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Payées</p>
                <p className="text-2xl font-bold text-gray-900">{paidEcheances}</p>
                <p className="text-sm text-green-600 font-medium">{formatCurrency(paidAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">{pendingEcheances}</p>
                <p className="text-sm text-orange-600 font-medium">{formatCurrency(pendingAmount)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">En retard</p>
                <p className="text-2xl font-bold text-gray-900">{overdueEcheances}</p>
                <p className="text-sm text-red-600 font-medium">{formatCurrency(overdueAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des échéances */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Liste des échéances ({filteredEcheanciers.length})
            </h3>
            <button 
              onClick={exportToPDF}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter PDF
            </button>
          </div>
          
          {filteredEcheanciers.length === 0 ? (
            <div className="p-6 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucune échéance trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('numero_echeance')}
                    >
                      <div className="flex items-center">
                        Échéance
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date_echeance')}
                    >
                      <div className="flex items-center">
                        Date
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('montant_prevu')}
                    >
                      <div className="flex items-center">
                        Montant
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('statut_echeance')}
                    >
                      <div className="flex items-center">
                        Statut
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Souscription
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEcheanciers.map((echeance: Echeancier) => (
                    <tr key={echeance.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Échéance #{echeance.numero_echeance}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(echeance.date_echeance)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(echeance.montant_prevu)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(echeance.statut_echeance)}`}>
                          {getStatusIcon(echeance.statut_echeance)}
                          <span className="ml-1">
                            {echeance.statut_echeance === 'PAYE' ? 'Payé' :
                             echeance.statut_echeance === 'EN_ATTENTE' ? 'En attente' :
                             echeance.statut_echeance === 'EN_RETARD' ? 'En retard' : echeance.statut_echeance}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const souscription = souscriptionsData?.results?.find((s: any) => s.id === echeance.souscription);
                          return (
                            <Link
                              to={`/subscriptions/${echeance.souscription}`}
                              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                              <Building className="w-4 h-4 mr-2" />
                              {souscription
                                ? `${souscription.type_maison_designation} - ${souscription.projet_nom}`
                                : `Souscription #${echeance.souscription}`
                              }
                            </Link>
                          );
                        })()}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          {echeance.statut_echeance === 'EN_ATTENTE' && (
                            <button className="text-blue-600 hover:text-blue-800">
                              Payer
                            </button>
                          )}
                          <Link
                            to={`/subscriptions/${echeance.souscription}`}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                        </div>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EcheanciersPage;
